import {
    BulletList,
    cssVar,
    FormInput,
    GenericModal,
    Header,
    Input,
    Label,
    Radio,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
// biome-ignore lint/correctness/noUnusedImports: required
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

const CurrentAmountWrapper = styled.div`
    text-align: center;
    margin-bottom: 16px;
`;

const RadioGroupWrapper = styled.div`
    margin-bottom: 24px;
`;

const AdjustmentModal = ({
    visible,
    mode,
    adjustment,
    adjustmentType,
    parentId,
    parentType,
    parentItem,
    policyTransactionGuid,
    initRule,
    validationRule,
    context,
    pendingSave,
    onClose,
    onSave,
}) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const intl = useIntl();
    const { isMobile: mobile } = useScreen();
    const [method, setMethod] = useState('flatAmount');
    const [value, setValue] = useState('');
    const [label, setLabel] = useState('');

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    useEffect(() => {
        if (adjustment && mode === 'edit') {
            setMethod(adjustment.adjustmentMethod || 'flatAmount');
            // FormInput expects numeric value for currency/percentage types
            setValue(adjustment.adjustmentMethodValue ?? '');
            setLabel(adjustment.description || '');
        } else {
            // Reset for add mode
            setMethod('flatAmount');
            setValue('');
            setLabel('');
        }
    }, [adjustment, mode, visible]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: 'CAD',
        }).format(amount);
    };

    const normalizeAdjustmentType = (type) => {
        // Normalize adjustment type to match constraint/validation keys in data.json
        // Examples:
        //   'premiumAdjustment' -> 'premium'
        //   'premium' -> 'premium'
        //   'feeAdjustment' -> 'fee'
        //   'fees' -> 'fee'
        //   'discountAdjustment' -> 'discount'
        //   'discounts' -> 'discount'
        //   'surchargeAdjustment' -> 'surcharge'
        //   'surcharges' -> 'surcharge'
        if (!type) return type;

        // Remove 'Adjustment' suffix if present
        let normalized = type.replace('Adjustment', '');

        // Remove trailing 's' for plurals (fees -> fee, discounts -> discount, surcharges -> surcharge)
        if (normalized.endsWith('s')) {
            normalized = normalized.slice(0, -1);
        }

        return normalized.toLowerCase();
    };

    const getMethodReferences = () => {
        const normalizedType = normalizeAdjustmentType(adjustmentType);
        // Map to backend structure: premiumInitRule, feeInitRule, etc.
        const ruleKey = `${normalizedType}InitRule`;
        const allowedMethods = initRule?.[ruleKey]?.allowedAdjustmentMethods || [
            'percentage',
            'flatAmount',
            'absoluteAmount',
        ];

        const methodOptions = {
            flatAmount: {
                code: 'flatAmount',
                value: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.flatAmount' }),
            },
            percentage: {
                code: 'percentage',
                value: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.percentage' }),
            },
            absoluteAmount: {
                code: 'absoluteAmount',
                value: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.absoluteAmount' }),
            },
        };

        return allowedMethods.map((method) => methodOptions[method]).filter(Boolean);
    };

    const getValidationRules = () => {
        const normalizedType = normalizeAdjustmentType(adjustmentType);
        // Map to backend structure: premiumValidationRule, feeValidationRule, etc.
        const ruleKey = `${normalizedType}ValidationRule`;
        const methodKey = method === 'percentage' ? 'percentageRule' : `${method}ValidationRule`;
        return validationRule?.[ruleKey]?.[methodKey];
    };

    const isValueValid = () => {
        // Handle both string and number values from FormInput
        const valueStr = typeof value === 'string' ? value : String(value);
        if (!valueStr || valueStr.trim() === '') return false;

        const numValue = Number.parseFloat(value);
        if (Number.isNaN(numValue)) return false;

        const rules = getValidationRules();
        if (rules) {
            if (numValue < rules.min || numValue > rules.max) {
                return false;
            }
        }

        return true;
    };

    const isValid = () => {
        const labelStr = typeof label === 'string' ? label : String(label);
        return labelStr.trim().length > 0 && isValueValid();
    };

    const getInputType = () => {
        // FormInput supports 'percent', 'currency', and 'number' types
        return method === 'percentage' ? 'percent' : 'currency';
    };

    const getPrecision = () => {
        return method === 'percentage' ? 1 : 2;
    };

    const getMinMaxValues = () => {
        const rules = getValidationRules();
        if (rules) {
            return {
                minValue: rules.min,
                maxValue: rules.max,
            };
        }
        return {
            minValue: undefined,
            maxValue: undefined,
        };
    };

    const handleSave = () => {
        if (isValid()) {
            // Convert value to number
            const numValue = typeof value === 'number' ? value : Number.parseFloat(value);

            // Map plural parentType to singular coverageItemType
            const coverageTypeMap = {
                premiums: 'premium',
                fees: 'fee',
                discounts: 'discount',
                surcharges: 'surcharge',
                premium: 'premium',
                fee: 'fee',
                discount: 'discount',
                surcharge: 'surcharge',
            };

            // Determine coverageItemType from parentType or adjustmentType
            let coverageItemType = adjustment?.coverageItemType;
            if (!coverageItemType) {
                // If parentType exists, use it; otherwise derive from adjustmentType
                const typeSource = parentType || adjustmentType;
                coverageItemType = coverageTypeMap[typeSource];
            }

            const payload = {
                code: adjustment?.code || `ADJ_${Date.now()}`,
                description: typeof label === 'string' ? label.trim() : String(label).trim(),
                adjustmentMethod: method,
                adjustmentMethodValue: numValue,
                coverageItemGuid: adjustment?.coverageItemGuid || parentId,
                policyTransactionGuid: adjustment?.policyTransactionGuid || policyTransactionGuid,
                coverageItemType: coverageItemType,
            };

            // Only include adjustmentGuid when editing existing adjustment
            if (mode === 'edit' && adjustment?.adjustmentGuid) {
                payload.adjustmentGuid = adjustment.adjustmentGuid;
            }

            onSave(payload);
        }
    };

    if (!visible) return null;

    // Check for loading state and errors specific to applyPolicyAdjustment operation
    const isLoading = Array.isArray(context?.loading)
        ? context.loading.some((x) => x.code === 'applyPolicyAdjustment' && x.value === true)
        : context?.loading;
    const hasError =
        context?.operation?.code === 'applyPolicyAdjustment' &&
        context?.operation?.status === false &&
        context?.operation?.errors?.length > 0;

    const getAdjustmentTypeLabel = () => {
        const normalizedType = normalizeAdjustmentType(adjustmentType);
        const typeLabels = {
            premium: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.premiumType' }),
            fee: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.feeType' }),
            discount: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.discountType' }),
            surcharge: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.surchargeType' }),
        };
        return typeLabels[normalizedType] || '';
    };

    const modalTitle =
        mode === 'edit'
            ? intl.formatMessage(
                  { id: 'Profile_UnderwriterCoverageAdjustment.modal.editTitle' },
                  { label: adjustment?.description },
              )
            : intl.formatMessage(
                  { id: 'Profile_UnderwriterCoverageAdjustment.modal.addTitle' },
                  { type: getAdjustmentTypeLabel() },
              );

    // Determine parent amount to display
    const getParentAmount = () => {
        if (mode === 'edit' && adjustment) {
            // When editing an adjustment, show the parent coverage amount
            return adjustment.coverageAmountFormatted || formatCurrency(adjustment.coverageAmount || 0);
        }
        if (mode === 'add' && parentItem) {
            // When adding to an existing parent, show parent amount
            return parentItem.amountFormatted || formatCurrency(parentItem.amount || 0);
        }
        // When adding to empty section (no parent), show $0
        return formatCurrency(0);
    };

    return (
        <GenericModal
            animate
            visible={visible}
            size={sizes.lg}
            closeEvent={onClose}
            items={
                <>
                    <Header size={sizes.md} text={modalTitle} bottom={sizes.sm} />
                    {hasError && (
                        <BulletList
                            size={sizes.md}
                            color={cssVar.colorError}
                            references={context.operation.errors}
                            bottom={sizes.md}
                        />
                    )}
                    <CurrentAmountWrapper>
                        <Label
                            text={`${intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.currentAmount' })}: ${getParentAmount()}`}
                            size={sizes.lg}
                        />
                    </CurrentAmountWrapper>
                    <RadioGroupWrapper>
                        <Label
                            text={
                                <FormattedMessage id='Profile_UnderwriterCoverageAdjustment.modal.adjustmentMethod' />
                            }
                            textAlign={alignments.left}
                            top={sizes.md}
                            bottom={sizes.xs}
                            size={sizes.sm}
                        />
                        <Radio
                            inline={!mobile}
                            componentCode='adjustmentMethod'
                            references={getMethodReferences()}
                            value={{ code: method }}
                            changeEvent={(selected) => setMethod(selected.code)}
                        />
                    </RadioGroupWrapper>
                    <FormInput
                        noFormMode
                        label={intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.value' })}
                        placeholder={intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.value' })}
                        value={value}
                        changeEvent={(val) => setValue(val)}
                        type={getInputType()}
                        precision={getPrecision()}
                        withCents={method !== 'percentage'}
                        {...getMinMaxValues()}
                        required
                        top={sizes.md}
                        bottom={sizes.md}
                    />
                    <Input
                        label={intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.description' })}
                        placeholder={intl.formatMessage({
                            id: 'Profile_UnderwriterCoverageAdjustment.modal.description',
                        })}
                        value={label}
                        changeEvent={(val) => setLabel(val)}
                        required
                        bottom={sizes.md}
                    />
                </>
            }
            actionButtons={[
                {
                    text: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.cancel' }),
                    clickEvent: onClose,
                    disabled: isLoading || pendingSave,
                },
                {
                    primary: true,
                    text: intl.formatMessage({ id: 'Profile_UnderwriterCoverageAdjustment.modal.apply' }),
                    disabled: !isValid() || isLoading || pendingSave,
                    clickEvent: handleSave,
                },
            ]}
        />
    );
};

export default AdjustmentModal;
